'use client';

import React, { useRef, useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';

import { useIsHydrated } from '@/hooks/useIsHydrated';
import {
  HTML_ALLOWED_ATTRS,
  HTML_ALLOWED_TAGS,
  HTML_FORBIDDEN_ATTRS,
  HTML_FORBIDDEN_TAGS,
} from '@/lib/htmlSanitizePolicy';

/** The shared policy (`htmlSanitizePolicy`) expressed as DOMPurify's config. */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: HTML_ALLOWED_TAGS,
  ALLOWED_ATTR: HTML_ALLOWED_ATTRS,
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: HTML_FORBIDDEN_TAGS,
  FORBID_ATTR: HTML_FORBIDDEN_ATTRS,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  SAFE_FOR_TEMPLATES: true,
};

interface IHtmlItemProps {
  html?: string;
  /**
   * `html` was already sanitized by the portal server (`sanitizePageHtml`).
   * Rendered as-is, on the server and in the browser alike, so the block is
   * part of the streamed document and hydration finds identical markup.
   */
  htmlSanitized?: boolean;
  uniqueIdentifier: string;
  className?: string;
  /**
   * Lazy-load the block's images unless the author set `loading` themselves.
   * Set for blocks below the page's primary card, whose images would otherwise
   * start downloading with the document and take bandwidth from that card's
   * thumbnail, the usual largest paint.
   */
  lazyImages?: boolean;
  onBlockClick?: () => void;
}

export const HtmlItem: React.FC<IHtmlItemProps> = ({ 
  html = '', 
  htmlSanitized = false,
  uniqueIdentifier, 
  className, 
  lazyImages = false,
  onBlockClick 
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const hydrated = useIsHydrated();

  const markup = useMemo(() => {
    if (htmlSanitized) {
      return lazyImages ? lazyLoadImages(html) : html;
    }

    // Unsanitized markup only arrives from client-side fetches (a logged-in
    // player's page, a language switch). DOMPurify needs a DOM: in the browser
    // that is free, on the server it means jsdom, which leaks (ACDEV-29355).
    if (!hydrated) {
      return null;
    }

    return sanitizeHtml(html, lazyImages);
  }, [html, htmlSanitized, lazyImages, hydrated]);

  if (markup === null) {
    return null;
  }

  return (
    <div 
      ref={itemRef}
      className={className}
      onClick={onBlockClick}
      style={{ width: '100%' }}
      data-html-block-id={uniqueIdentifier}
      // Safe to use dangerouslySetInnerHTML because content is sanitized, by
      // the server (`htmlSanitized`) or by DOMPurify above
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
};

export const sanitizeHtml = (html: string, lazyImages: boolean): string => {
  const body = DOMPurify.sanitize(html, { ...SANITIZE_CONFIG, RETURN_DOM: true }) as HTMLElement;

  if (lazyImages) {
    body.querySelectorAll('img:not([loading])').forEach((img: Element) => {
      img.setAttribute('loading', 'lazy');
    });
  }

  return body.innerHTML;
};

/**
 * String-level counterpart of the DOM pass in `sanitizeHtml`, for markup that
 * is already sanitized and therefore has no DOM to walk on the server. The
 * sanitizer serializes every attribute value quoted and with `>` escaped, so an
 * `img` tag ends at the first `>` after it.
 */
export const lazyLoadImages = (sanitizedHtml: string): string =>
  sanitizedHtml.replace(/<img\b(?![^>]*\sloading=)/gi, '<img loading="lazy"');
